import { type Ref } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "~/atoms/checkbox";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";
import { BulkSelectionBar } from "~/molecules/BulkSelectionBar";
import { type Option } from "~/molecules/ControlledCombobox";
import {
  DraftTableRow,
  type DraftRowHoverCol,
} from "~/molecules/onboard-documents/DraftTableRow";
import { hiddenNativeScrollbarClass } from "~/molecules/StickyHorizontalScrollbar";
import { useBulkHoverHighlight } from "~/hooks/useBulkHoverHighlight";
import { type DocumentReleaseSummary } from "~/api/types";
import { findDuplicateDocumentRelease } from "~/helpers/document";
import { type DraftRow, type RowPatch } from "~/helpers/draftRows";

interface DraftsTableProps {
  wrapperRef?: Ref<HTMLDivElement>;
  rows: DraftRow[];
  selectedIds: Set<string>;
  allSelected: boolean;
  isCreating: boolean;
  tierOptions: Option[] | undefined;
  categoryOptions: Option[] | undefined;
  releases: DocumentReleaseSummary[] | undefined;
  createdCount: number;
  onToggleAll: () => void;
  onToggleRow: (id: string) => void;
  onUnselectAll: () => void;
  onUpdateRow: (id: string, patch: RowPatch, opts?: { bulk?: boolean }) => void;
  onCreateRow: (row: DraftRow) => void;
  onRemoveRow: (id: string) => void;
}

export const DraftsTable = ({
  wrapperRef,
  rows,
  selectedIds,
  allSelected,
  isCreating,
  tierOptions,
  categoryOptions,
  releases,
  createdCount,
  onToggleAll,
  onToggleRow,
  onUnselectAll,
  onUpdateRow,
  onCreateRow,
  onRemoveRow,
}: DraftsTableProps) => {
  const { t } = useTranslation();
  const { onHover, onHoverLeave, isHighlighted } = useBulkHoverHighlight<
    string,
    DraftRowHoverCol
  >(selectedIds);

  return (
    <div className="flex flex-col gap-1">
      <BulkSelectionBar
        count={selectedIds.size}
        onUnselectAll={onUnselectAll}
        disabled={isCreating}
      />
      <Table
        wrapperRef={wrapperRef}
        wrapperClassName={`rounded-md border ${hiddenNativeScrollbarClass}`}
      >
        <TableHeader className="bg-muted">
          <TableRow>
            <TableHead>
              <Checkbox
                checked={allSelected}
                onCheckedChange={onToggleAll}
                aria-label={t(($) => $.commons.buttons.selectAll)}
              />
            </TableHead>
            <TableHead>{t(($) => $.commons.document.title)}</TableHead>
            <TableHead>{t(($) => $.commons.document.tier)}</TableHead>
            <TableHead>{t(($) => $.commons.document.category)}</TableHead>
            <TableHead className="text-right">
              {t(
                ($) =>
                  $.library.onboardDocuments.steps.createDrafts.createdLabel,
                { createdCount, totalCount: rows.length },
              )}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const hasDuplicate = !!findDuplicateDocumentRelease({
              releases,
              title: row.title,
              categoryId:
                row.categoryId === null ? null : Number(row.categoryId),
            });

            return (
              <DraftTableRow
                key={row.id}
                row={row}
                isSelected={selectedIds.has(row.id)}
                isCreating={isCreating}
                hasDuplicate={hasDuplicate}
                tierOptions={tierOptions}
                categoryOptions={categoryOptions}
                onToggleSelection={() => onToggleRow(row.id)}
                onUpdate={(patch, opts) => onUpdateRow(row.id, patch, opts)}
                onCreate={() => onCreateRow(row)}
                onRemove={() => onRemoveRow(row.id)}
                onHoverCell={(col) => onHover(row.id, col)}
                onHoverLeave={onHoverLeave}
                isBulkHighlighted={(col) => isHighlighted(row.id, col)}
              />
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};
